import React from "react";

import { faBriefcase } from "@fortawesome/pro-light-svg-icons/faBriefcase";
import Item from "antd/es/descriptions/Item";
import Tag from "antd/es/tag";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import { Deal } from "@mapmycustomers/shared/types/entity";

import CadenceStatusLink from "@app/component/cadence/CadenceStatusLink";
import EntityTag from "@app/component/EntityTag";
import i18nService from "@app/config/I18nService";
import { formatRawDate } from "@app/util/formatters";
import { parseApiDateWithUtcTz } from "@app/util/parsers";
import { escapeForHtml } from "@app/util/text";
import { getEntityTypeDisplayName } from "@app/util/ui";

import BaseInfoWindow from "./BaseInfoWindow";

interface Props {
  callbackName?: string;
  companyClickCallbackName?: string;
  dateFormat?: string;
  deal: Deal;
  personClickCallbackName?: string;
}

const DealInfoWindow: React.FC<Props> = ({
  callbackName,
  companyClickCallbackName,
  dateFormat = "PPp",
  deal,
  personClickCallbackName,
}) => {
  // not using useIntl because this component is rendered separately from the main component tree
  const intl = i18nService.getIntl();
  if (!intl) {
    return null;
  }

  return (
    <BaseInfoWindow
      footer={
        <div
          dangerouslySetInnerHTML={{
            __html: `
          <button
            class="ant-btn ant-btn-primary ant-btn-block"
            onclick="${callbackName}(${deal.id})"
            type="button"
          >
            ${intl.formatMessage({
              id: "simpleEntityMap.infoWindow.deal.preview",
              defaultMessage: "Preview Deal",
              description:
                "Preview Deal button in standard info window for Deals in a Simple Entity Map",
            })}
          </button>
        `,
          }}
        />
      }
      title={deal.name}
      titleIcon={faBriefcase}
    >
      {deal.amount ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.deal.amount",
            defaultMessage: "Amount",
            description:
              "Amount field name in standard info window for Deals in a Simple Entity Map",
          })}
        >
          {intl.formatNumber(deal.amount, {
            currency: "USD", // TODO: why default currency, can we change this?
            minimumFractionDigits: 0,
            style: "currency",
          })}
        </Item>
      ) : null}
      {deal.stage ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.deal.stage",
            defaultMessage: "Stage",
            description:
              "Stage field name in standard info window for Deals in a Simple Entity Map",
          })}
        >
          <EntityTag entity={deal.stage} />
        </Item>
      ) : null}
      {deal.closingDate ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.deal.closingDate",
            defaultMessage: "Closing Date",
            description:
              "Closing Date field name in standard info window for Deals in a Simple Entity Map",
          })}
        >
          {formatRawDate(deal.closingDate, "PPP", "", parseApiDateWithUtcTz)}
        </Item>
      ) : null}
      {deal.account ? (
        <Item
          label={getEntityTypeDisplayName(intl, EntityType.COMPANY, {
            lowercase: false,
            plural: false,
          })}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: `
               <a onclick="${companyClickCallbackName}(${deal.account.id})">
                ${escapeForHtml(deal.account.name)}
              </a>
              `,
            }}
          />
        </Item>
      ) : null}
      {deal.contact ? (
        <Item
          label={getEntityTypeDisplayName(intl, EntityType.PERSON, {
            lowercase: false,
            plural: false,
          })}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: `
               <a onclick="${personClickCallbackName}(${deal.contact.id})">
                ${escapeForHtml(deal.contact.name)}
              </a>
              `,
            }}
          />
        </Item>
      ) : null}
      {deal.createdAt ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.deal.createdAt",
            defaultMessage: "Created At",
            description:
              "Created At field name in standard info window for Deals in a Simple Entity Map",
          })}
        >
          <Tag>{formatRawDate(deal.createdAt, dateFormat)}</Tag>
        </Item>
      ) : null}
      {deal.daysInStage ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.deal.daysInStage.label",
            defaultMessage: "Days in stage",
            description:
              "Days in stage field name in standard info window for Deals in a Simple Entity Map",
          })}
        >
          {intl.formatMessage(
            {
              id: "simpleEntityMap.infoWindow.deal.daysInStage.value",
              defaultMessage: "{days} {days, plural, one {day} other {days}}",
              description:
                "Days in stage field value in standard info window for Deals in a Simple Entity Map",
            },
            {
              days: deal.daysInStage,
            }
          )}
        </Item>
      ) : null}
      {deal.rottingDaysOut && deal.rottingDaysOut > 0 ? (
        <Item
          label={intl.formatMessage({
            id: "simpleEntityMap.infoWindow.deal.rottingDaysOut.label",
            defaultMessage: "Deal status",
            description:
              "Deal status field name in standard info window for Deals in a Simple Entity Map",
          })}
        >
          <CadenceStatusLink active={false} type="warning">
            {intl.formatMessage(
              {
                id: "simpleEntityMap.infoWindow.deal.rottingDaysOut.value",
                defaultMessage:
                  "Rotting in stage for {days} {days, plural, one {day} other {days}}",
                description:
                  "Deal status field value in standard info window for Deals in a Simple Entity Map",
              },
              {
                days: deal.rottingDaysOut,
              }
            )}
          </CadenceStatusLink>
        </Item>
      ) : null}
    </BaseInfoWindow>
  );
};

export default DealInfoWindow;
